<template>
  <div
    class="new-order-aside-component"
    :class="{
      'new-order-aside-component--compact': isCompactScreen,
      'new-order-aside-component--collapsed': isCollapsed,
      'new-order-aside-component--over': isExpandedOver,
    }"
  >
    <!-- Развёрнутый сайдбар на компактном десктопе лежит поверх страницы -->
    <v-overlay
      :value="isExpandedOver"
      z-index="6"
      @click.native="compactExpanded = false"
    />
    <v-navigation-drawer
      id="default-drawer"
      dark
      :right="$vuetify.rtl"
      mini-variant-width="80"
      app
      :width="drawerWidth"
    >
      <div
        v-if="showAttentionMessage"
        class="attention-section d-flex align-center justify-center"
      >
        <div
          class="attention-window d-flex flex-column justify-center align-center"
        >
          <span class="title font-weight-regular">Внимание</span>
          <span class="sub-title">Сначала создайте клиента!</span>
          <v-btn
            class="button-apply"
            color="#FF9100"
            depressed
            @click="showAttentionMessage = false"
          >
            Ok
          </v-btn>
        </div>
      </div>

      <perfect-scrollbar :options="{ suppressScrollX: true }">
        <div class="aside-container">
          <div class="aside-container__top">
            <aside-logo v-show="!isCollapsed" />

            <v-btn
              v-if="isCompactScreen"
              class="button-toggle-aside"
              icon
              :title="compactExpanded ? 'Свернуть панель' : 'Развернуть панель'"
              @click="compactExpanded = !compactExpanded"
            >
              <font-awesome-icon :icon="compactExpanded ? 'chevron-left' : 'chevron-right'" />
            </v-btn>

            <span v-if="isCollapsed" class="strip-time">{{ hours }}:{{ minutes }}</span>

            <!-- v-if, не v-show: у .d-flex от Vuetify display с !important -->
            <div v-if="!isCollapsed" class="d-flex justify-lg-space-between">
              <div class="clocks-section">
                <span class="aside-title">Новый заказ</span>
                <span class="date">{{ day }} {{ month }} {{ week }} {{ hours }}:{{ minutes }}</span>
              </div>
              <div class=" aggregate-section d-flex align-center ">
                <aggregator-select />
              </div>
            </div>
            <v-btn
              class="button-back text-normalize font-weight-medium"
              width="100%"
              :disabled="checkingBasketItemsChanges"
              :title="isCollapsed ? 'Назад к списку заказов' : undefined"
              @click="onBackToListClick"
            >
              <font-awesome-icon :icon="isCollapsed ? 'times' : 'chevron-left'" />
              <span class="button-back__label">Назад к списку заказов</span>
            </v-btn>

            <primary-confirm-dialog
              v-model="showExitConfirmDialog"
              confirm-text="Продолжить"
              @confirm="goToOrdersList"
            >
              <template v-slot:title>
                Вы действительно хотите закрыть заказ и перейти в список?
              </template>
            </primary-confirm-dialog>

            <order-help v-show="!isCollapsed" />
          </div>
          <div class="aside-container__bottom">
            <v-menu
              :collapsed="isCollapsed"
              :compact="isCompactScreen"
              @showAttentionMessage="showAttentionMessage = true"
            />
          </div>
        </div>
      </perfect-scrollbar>
      <div class="pt-12" />
    </v-navigation-drawer>
  </div>
</template>

<script>
  import { mapState } from 'vuex'
  import OrderHelp from './OrderHelp.vue'
  import VMenu from './Menu.vue'
  import mixinGetDatatime from '@/mixins/get-datatime'
  import compactScreen from '@/mixins/compact-screen'
  import orderEdit from '@/mixins/order-edit'
  import AggregatorSelect from './AggregatorSelect.vue'
  import AsideLogo from '@/components/aside/AsideLogo.vue'
  import _ from 'lodash'
  import PrimaryConfirmDialog from '@/components/ui/dialogs/PrimaryConfirmDialog.vue'

  // Ширина полоски на компактном десктопе, как у сайдбара списка заказов
  const COLLAPSED_WIDTH = 80

  export default {
    name: 'NewOrderAside',
    components: {
      PrimaryConfirmDialog,
      AsideLogo,
      OrderHelp,
      VMenu,
      AggregatorSelect,
    },
    mixins: [mixinGetDatatime, compactScreen, orderEdit],
    data () {
      return {
        showAttentionMessage: false,
        asideWidth: 260,
        showExitConfirmDialog: false,
        compactExpanded: false,
      }
    },
    computed: {
      ...mapState({
        currentStep: state => state.newOrder.currentStep,
        newOrderBasketId: state => state.baskets.basket && state.baskets.basket.id,
        orderId: state => state.fastOrder.orderId,
        checkingBasketItemsChanges: state => state.editSession.checkingBasketItemsChanges,
      }),
      isCollapsed () {
        return this.isCompactScreen && !this.compactExpanded
      },
      isExpandedOver () {
        return this.isCompactScreen && this.compactExpanded
      },
      // Vuetify всегда считает сайдбар полоской и отступ страницы не меняет;
      // развёрнутую ширину даёт CSS, поэтому панель ложится поверх контента
      drawerWidth () {
        return this.isCompactScreen ? COLLAPSED_WIDTH : this.asideWidth
      },
      // Всё, после чего оператору нужно видеть страницу, а не панель: шаг,
      // категория меню, окно клиента, акции
      pageStateKey () {
        const state = this.orderState
        return [
          state.currentStep,
          state.currentCategory,
          state.currentOtherWindow,
          state.showPromotionsWindow,
        ].join('|')
      },
    },
    watch: {
      $route () {
        this.compactExpanded = false
      },
      isCompactScreen () {
        this.compactExpanded = false
      },
      pageStateKey () {
        this.compactExpanded = false
      },
    },
    mounted () {
      this.$nextTick(() => {
        // this.show = window.innerWidth > 1024
        this.calcAsideWidth()
      })
    },
    created () {
      window.addEventListener('resize', this.calcAsideWidthOnResize)
    },
    destroyed () {
      window.removeEventListener('resize', this.calcAsideWidthOnResize)
    },
    methods: {
      calcAsideWidth () {
        this.asideWidth = window.innerWidth < 1900 ? 260 : 326
      },
      calcAsideWidthOnResize: _.debounce(function () {
        this.calcAsideWidth()
      }, 100),
      onBackToListClick () {
        // Для нового заказа с корзиной конфирм показывает router guard - там есть «Свернуть заказ»
        if (this.$route.name === 'newOrder' && this.newOrderBasketId) {
          this.$router.push('/orders')
          return
        }
        this.showExitConfirmDialog = true
      },
      goToOrdersList () {
        this.showExitConfirmDialog = false
        this.$store.commit('app/setAllowPageExitWithoutConfirm', true)
        this.$router.push('/orders')
      },
    },
  }
</script>

<style lang="less">
.new-order-aside-component {
  .v-list-item {
    margin-bottom: 8px;
  }

  .theme--dark.v-navigation-drawer {
    background-color: #040404;
  }

  .v-list-item::before,
  .v-list-item::after {
    display: none;
  }

  .v-list-group__header__prepend-icon,
  .v-list-item__icon {
    margin-top: 12px;
    margin-bottom: 12px;
    margin-left: 4px;
  }

  &.v-navigation-drawer--mini-variant {
    .v-list-item {
      justify-content: flex-start !important;
    }
  }

  .ps {
    height: 100%;
  }

  .v-navigation-drawer__content {
    height: auto;
    min-height: 100%;
    overflow-y: hidden;
  }
}

.new-order-aside-component {
  position: relative;
  z-index: 0;

  .attention-section {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(#040404, 0.85);
    z-index: 1;
    .attention-window {
      background-color: #ffffff;
      padding: 54px 50px 26px;
      border-radius: 10px;
      .title {
        display: block;
        margin-bottom: 8px;
        font-size: 25px !important;
        line-height: 1;
      }
      .sub-title {
        display: block;
        font-size: 14px;
        line-height: 17px;
        margin-bottom: 35px;
      }
      .button-apply {
        font-size: 15px;
        line-height: 21px;
        height: 80px;
        width: 145px;
      }
    }
  }

  .aside-container {
    padding: 0 16px 0 18px;
    font-size: 12px;
    display: flex;
    flex-direction: column;
    min-height: 100%;
    justify-content: space-between;

    .media-xl({
      font-size: 14px;
    });

    .clocks-section {
      margin-bottom: 17px;
      .aside-title {
        display: block;
        color: @color-primary;
        font-size: 1.6em;
        line-height: 1;
      }
      .date {
        color: #ffffff;
        font-size: 1em;
        line-height: 17px;
      }
    }
    .aggregate-section {
      margin-bottom: 17px;
    }
    .button-back {
      font-size: 1.14em;
      line-height: 19px;
      height: 50px;
      background-color: #191919 !important;
      svg {
        margin-right: 12px;
        .fill-svg(#0033ff);
      }
    }

    .button-toggle-aside {
      display: flex;
      width: 44px;
      height: 44px;
      margin: 12px auto 10px;
      background-color: #191919;
      border-radius: 10px;

      // размер иконки FontAwesome считает от font-size
      svg {
        font-size: 20px;
        height: 1em;
        .fill-svg(#ffffff);
      }
    }

    .strip-time {
      display: block;
      margin-bottom: 12px;
      color: #ffffff;
      font-size: 18px;
      line-height: 1;
      text-align: center;
    }
  }

  // Компактный десктоп. У корня свой контекст наложения с z-index 0 - поднимаем
  // его целиком и в полоске, и в развёрнутом виде: иначе кнопки страницы рисуются
  // поверх панели, пока она сворачивается (ширина анимируется 0.2s)
  &--compact {
    z-index: 7;
  }

  // Полоска: кнопка разворачивания, время, «назад», внизу - карточка клиента
  &--collapsed {
    .aside-container {
      padding: 0 14px;
    }

    // «Назад к списку» в полоске - заметная: розовая с белым крестиком.
    // Ширину 100% кнопке задаёт инлайновый стиль от пропа width
    .aside-container .button-back {
      width: 52px !important;
      min-width: 52px !important;
      padding: 0 !important;
      background-color: @color-pink !important;

      // размер иконки FontAwesome считает от font-size (ширина в em)
      svg {
        font-size: 26px;
        height: 1em;
        margin-right: 0;
        .fill-svg(#ffffff);
      }
    }

    .button-back__label {
      display: none;
    }
  }

  // Развёрнутая поверх страницы: кнопка сворачивания в строке заголовка
  &--over {
    .v-navigation-drawer {
      width: 260px !important;
    }

    .aside-container .button-toggle-aside {
      position: absolute;
      top: 58px;
      right: 16px;
      margin: 0;
    }
  }
}
</style>
